import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Tooltip, Typography } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ENV from "../../../config/env";
import { comprimirImagen, formatearPeso } from "../../../utils/comprimirImagen";
import * as clienteService from "../services/clienteService";

// Son logos o fotos de referencia del servicio: no hace falta calidad ni tamaño de foto original.
const OPCIONES_COMPRESION = { maxLado: 640, calidad: 0.7 };

const GRID_SX = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))", gap: 1 };
const MINIATURA_SX = { position: "relative", aspectRatio: "1", borderRadius: 1, overflow: "hidden", border: 1, borderColor: "divider", bgcolor: "action.hover" };

const BotonAgregar = ({ onElegir, disabled, ocupado, etiqueta = "Agregar imágenes" }) => {
  const ref = useRef(null);
  return (
    <>
      <input
        ref={ref} type="file" accept="image/*" multiple style={{ display: "none" }}
        onChange={(e) => { const lista = Array.from(e.target.files || []); e.target.value = ""; if (lista.length) onElegir(lista); }}
      />
      <Button
        size="small" variant="outlined" disabled={disabled || ocupado} onClick={() => ref.current?.click()}
        startIcon={ocupado ? <CircularProgress size={14} /> : <AddPhotoAlternateIcon fontSize="small" />}
      >
        {ocupado ? "Optimizando…" : etiqueta}
      </Button>
    </>
  );
};

const BOTON_SOBRE_FOTO = { position: "absolute", bgcolor: "rgba(0,0,0,0.55)", color: "#fff", p: 0.25, "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } };

// esPerfil/onPerfil = estrella de imagen de perfil; onQuitar = botón X.
const Miniatura = ({ src, titulo, pie, onClick, onQuitar, deshabilitado, esPerfil, onPerfil }) => (
  <Box sx={MINIATURA_SX}>
    <Box component="img" src={src} alt={titulo} onClick={onClick} sx={{ width: "100%", height: "100%", objectFit: "cover", cursor: onClick ? "zoom-in" : "default", display: "block" }} />
    {pie && <Typography sx={{ position: "absolute", left: 0, right: 0, bottom: 0, px: 0.5, fontSize: 10, color: "#fff", bgcolor: "rgba(0,0,0,0.55)", textAlign: "center" }}>{pie}</Typography>}
    {(onPerfil || esPerfil) && (
      <Tooltip title={esPerfil ? "Imagen de perfil del servicio" : "Usar como imagen de perfil"}>
        <span>
          <IconButton
            size="small" disabled={deshabilitado || !onPerfil || esPerfil} onClick={onPerfil}
            sx={{ ...BOTON_SOBRE_FOTO, top: 2, left: 2, color: esPerfil ? "#ffc107" : "#fff", "&.Mui-disabled": { color: esPerfil ? "#ffc107" : "rgba(255,255,255,0.4)" } }}
          >
            {esPerfil ? <StarIcon sx={{ fontSize: 15 }} /> : <StarBorderIcon sx={{ fontSize: 15 }} />}
          </IconButton>
        </span>
      </Tooltip>
    )}
    {onQuitar && (
      <Tooltip title="Quitar">
        <span>
          <IconButton size="small" disabled={deshabilitado} onClick={onQuitar} sx={{ ...BOTON_SOBRE_FOTO, top: 2, right: 2 }}>
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </span>
      </Tooltip>
    )}
  </Box>
);

export const VisorImagen = ({ imagen, onClose }) => (
  <Dialog open={!!imagen} onClose={onClose} maxWidth="lg">
    <DialogContent sx={{ p: 0, bgcolor: "#000", display: "grid", placeItems: "center" }}>
      {imagen && <Box component="img" src={imagen.src} alt={imagen.titulo} sx={{ maxWidth: "100%", maxHeight: "85vh", display: "block" }} />}
    </DialogContent>
    <DialogActions><Button onClick={onClose}>Cerrar</Button></DialogActions>
  </Dialog>
);

// Prepara las fotos elegidas: las comprime y devuelve { id, file, preview, original, final }.
const prepararFotos = async (lista) => {
  const resultados = await Promise.all(lista.filter((f) => f.type.startsWith("image/")).map((f) => comprimirImagen(f, OPCIONES_COMPRESION)));
  return resultados.map((r, i) => ({ id: `${Date.now()}-${i}-${Math.random()}`, file: r.file, preview: URL.createObjectURL(r.file), original: r.original, final: r.final }));
};

const resumenAhorro = (items) => {
  const orig = items.reduce((a, x) => a + x.original, 0);
  const fin = items.reduce((a, x) => a + x.final, 0);
  return fin < orig ? `${formatearPeso(orig)} → ${formatearPeso(fin)} (optimizadas)` : formatearPeso(fin);
};

const urlImagen = (img) => `${ENV.API_BASE_URL}${img.archivo_url}`;

// El perfil es la marcada como principal; si ninguna lo está, la más antigua (la lista llega de más nueva a más antigua).
const urlPerfilDe = (lista) => (lista.find((x) => Number(x.principal) === 1) || lista[lista.length - 1])?.archivo_url || null;

/* ---------- Alta: elige imágenes que se suben al guardar el servicio ---------- */

export function SelectorImagenes({ value, onChange, disabled }) {
  const [ocupado, setOcupado] = useState(false);
  const [visor, setVisor] = useState(null);
  const valorRef = useRef(value);
  valorRef.current = value;
  useEffect(() => () => valorRef.current.forEach((x) => URL.revokeObjectURL(x.preview)), []);

  const agregar = async (lista) => {
    setOcupado(true);
    try {
      onChange([...value, ...(await prepararFotos(lista))]);
    } finally {
      setOcupado(false);
    }
  };
  const quitar = (id) => {
    const x = value.find((i) => i.id === id);
    if (x) URL.revokeObjectURL(x.preview);
    onChange(value.filter((i) => i.id !== id));
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: value.length ? 1 : 0, flexWrap: "wrap" }}>
        <BotonAgregar onElegir={agregar} disabled={disabled} ocupado={ocupado} />
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {value.length ? `${value.length} imagen(es) · ${resumenAhorro(value)} · se suben al guardar` : "Opcional. Las fotos se optimizan automáticamente antes de subirse."}
        </Typography>
      </Box>
      {value.length > 0 && (
        <Box sx={GRID_SX}>
          {value.map((x) => (
            <Miniatura key={x.id} src={x.preview} titulo={x.file.name} pie={formatearPeso(x.final)} onClick={() => setVisor({ src: x.preview, titulo: x.file.name })} onQuitar={() => quitar(x.id)} deshabilitado={disabled} />
          ))}
        </Box>
      )}
      <VisorImagen imagen={visor} onClose={() => setVisor(null)} />
    </Box>
  );
}

// Sube las imágenes elegidas en el alta; se llama después de crear el servicio.
export const subirImagenesNuevas = (codCliente, codServicioCliente, items) =>
  clienteService.uploadImagenesServicio(codCliente, codServicioCliente, items.map((x) => x.file));

/* ---------- Edición: los cambios quedan pendientes y se aplican al pulsar Guardar ---------- */

// Uso: const imagenes = useImagenesEdicion(codCliente, cod); <ImagenesServicioEditor ctrl={imagenes} />
// y, al guardar el servicio, `await imagenes.aplicar()` (devuelve la URL de la imagen de perfil resultante).
export function useImagenesEdicion(codCliente, codServicioCliente) {
  const [existentes, setExistentes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [nuevas, setNuevas] = useState([]);  // fotos elegidas, aún sin subir
  const [quitar, setQuitar] = useState([]);  // ids de existentes marcadas para eliminar
  const [perfil, setPerfil] = useState(null); // { nueva: boolean, id } elegida por el usuario
  const [ocupado, setOcupado] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const nuevasRef = useRef([]);
  nuevasRef.current = nuevas;
  useEffect(() => () => nuevasRef.current.forEach((x) => URL.revokeObjectURL(x.preview)), []);

  const cargar = useCallback(async () => {
    try {
      const { data } = await clienteService.getImagenesServicio(codCliente, codServicioCliente);
      const lista = Array.isArray(data) ? data : [];
      setExistentes(lista);
      return lista;
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudieron cargar las imágenes.");
      return [];
    } finally {
      setCargando(false);
    }
  }, [codCliente, codServicioCliente]);

  useEffect(() => { cargar(); }, [cargar]);

  const visibles = existentes.filter((x) => !quitar.includes(x.id));
  const claveDe = (nueva, id) => `${nueva ? "n" : "e"}:${id}`;
  const perfilValido = perfil && (perfil.nueva ? nuevas.some((x) => x.id === perfil.id) : visibles.some((x) => x.id === perfil.id));
  const principalServidor = visibles.find((x) => Number(x.principal) === 1) || visibles[visibles.length - 1];
  const clavePerfil = perfilValido
    ? claveDe(perfil.nueva, perfil.id)
    : principalServidor ? claveDe(false, principalServidor.id) : nuevas[0] ? claveDe(true, nuevas[0].id) : null;
  const hayCambios = nuevas.length > 0 || quitar.length > 0 || !!perfilValido;

  // URL (o vista previa) de la imagen que será la de perfil.
  const urlPerfil = (() => {
    if (!clavePerfil) return null;
    const [tipo, id] = clavePerfil.split(":");
    if (tipo === "n") return nuevas.find((x) => String(x.id) === id)?.preview || null;
    const e = visibles.find((x) => String(x.id) === id);
    return e ? urlImagen(e) : null;
  })();

  // comoPerfil: la primera foto elegida pasa a ser la imagen de perfil (botón de la cámara).
  const agregar = async (lista, comoPerfil = false) => {
    setError("");
    setOcupado(true);
    try {
      const items = await prepararFotos(lista);
      if (!items.length) return setError("Elige archivos de imagen.");
      setNuevas((prev) => [...prev, ...items]);
      if (comoPerfil) setPerfil({ nueva: true, id: items[0].id });
    } finally {
      setOcupado(false);
    }
  };
  const cambiarFoto = (lista) => agregar(lista.slice(0, 1), true);
  const quitarNueva = (id) => {
    const x = nuevas.find((i) => i.id === id);
    if (x) URL.revokeObjectURL(x.preview);
    setNuevas((prev) => prev.filter((i) => i.id !== id));
  };
  const quitarExistente = (id) => setQuitar((prev) => [...prev, id]);
  const deshacerQuitar = () => setQuitar([]);
  const elegirPerfil = (nueva, id) => setPerfil({ nueva, id });

  const aplicar = async () => {
    if (!hayCambios) return undefined;
    setAplicando(true);
    try {
      for (const id of quitar) {
        try {
          await clienteService.deleteImagenServicio(codCliente, codServicioCliente, id);
        } catch (e) {
          if (e?.response?.status !== 404) throw e; // ya estaba borrada (reintento)
        }
      }
      let creadas = [];
      if (nuevas.length) {
        const { data } = await clienteService.uploadImagenesServicio(codCliente, codServicioCliente, nuevas.map((x) => x.file));
        creadas = Array.isArray(data) ? data : [];
      }
      if (perfilValido) {
        const idPerfil = perfil.nueva ? creadas[nuevas.findIndex((x) => x.id === perfil.id)]?.id : perfil.id;
        if (idPerfil) await clienteService.setImagenPrincipalServicio(codCliente, codServicioCliente, idPerfil);
      }
      nuevas.forEach((x) => URL.revokeObjectURL(x.preview));
      setNuevas([]);
      setQuitar([]);
      setPerfil(null);
      return urlPerfilDe(await cargar());
    } finally {
      setAplicando(false);
    }
  };

  return { existentes, visibles, nuevas, quitar, cargando, error, setError, ocupado, aplicando, hayCambios, clavePerfil, urlPerfil, claveDe, agregar, cambiarFoto, quitarNueva, quitarExistente, deshacerQuitar, elegirPerfil, aplicar };
}

// Foto de perfil del servicio (grande, para la cabecera del formulario). La cámara elige una foto nueva como perfil;
// como todo cambio de imágenes, se aplica al guardar.
export function FotoPerfilServicio({ ctrl, nombre, size = 96 }) {
  const ref = useRef(null);
  const [visor, setVisor] = useState(null);
  const iniciales = String(nombre || "?").trim().slice(0, 2).toUpperCase();
  const ocupado = ctrl.ocupado || ctrl.aplicando;
  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <Avatar
        variant="rounded" src={ctrl.urlPerfil || undefined} alt={nombre}
        onClick={ctrl.urlPerfil ? () => setVisor({ src: ctrl.urlPerfil, titulo: nombre }) : undefined}
        sx={{ width: size, height: size, borderRadius: 2, fontSize: size * 0.34, fontWeight: 800, bgcolor: ctrl.urlPerfil ? "transparent" : "primary.main", color: "primary.contrastText", cursor: ctrl.urlPerfil ? "zoom-in" : "default", border: 3, borderColor: "background.paper", boxShadow: 2 }}
      >
        {iniciales}
      </Avatar>
      <input
        ref={ref} type="file" accept="image/*" style={{ display: "none" }}
        onChange={(e) => { const lista = Array.from(e.target.files || []); e.target.value = ""; if (lista.length) ctrl.cambiarFoto(lista); }}
      />
      <Tooltip title="Cambiar foto de perfil">
        <span>
          <IconButton
            size="small" disabled={ocupado} onClick={() => ref.current?.click()}
            sx={{ position: "absolute", right: -8, bottom: -8, bgcolor: "primary.main", color: "primary.contrastText", boxShadow: 2, "&:hover": { bgcolor: "primary.dark" }, "&.Mui-disabled": { bgcolor: "action.disabledBackground" } }}
          >
            {ctrl.ocupado ? <CircularProgress size={16} color="inherit" /> : <PhotoCameraIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <VisorImagen imagen={visor} onClose={() => setVisor(null)} />
    </Box>
  );
}

export function ImagenesServicioEditor({ ctrl }) {
  const [visor, setVisor] = useState(null);
  const bloqueado = ctrl.aplicando;
  const vacio = !ctrl.cargando && ctrl.visibles.length === 0 && ctrl.nuevas.length === 0;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
        {ctrl.cargando && <CircularProgress size={14} />}
        <Typography sx={{ fontSize: 12, color: "text.secondary", flex: 1 }}>La estrella marca la imagen de perfil del servicio.</Typography>
        <BotonAgregar onElegir={ctrl.agregar} ocupado={ctrl.ocupado} disabled={bloqueado} etiqueta="Agregar" />
      </Box>
      {ctrl.error && <Alert severity="error" onClose={() => ctrl.setError("")} sx={{ mb: 1, py: 0 }}>{ctrl.error}</Alert>}
      {vacio && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Este servicio no tiene imágenes. Usa “Agregar” para elegir fotos.</Typography>}
      {(ctrl.visibles.length > 0 || ctrl.nuevas.length > 0) && (
        <Box sx={GRID_SX}>
          {ctrl.visibles.map((img) => (
            <Miniatura
              key={`e${img.id}`} src={urlImagen(img)} titulo={img.nombre_original} pie={formatearPeso(img.tamano)} deshabilitado={bloqueado}
              onClick={() => setVisor({ src: urlImagen(img), titulo: img.nombre_original })} onQuitar={() => ctrl.quitarExistente(img.id)}
              esPerfil={ctrl.clavePerfil === ctrl.claveDe(false, img.id)} onPerfil={() => ctrl.elegirPerfil(false, img.id)}
            />
          ))}
          {ctrl.nuevas.map((x) => (
            <Miniatura
              key={`n${x.id}`} src={x.preview} titulo={x.file.name} pie={`Nueva · ${formatearPeso(x.final)}`} deshabilitado={bloqueado}
              onClick={() => setVisor({ src: x.preview, titulo: x.file.name })} onQuitar={() => ctrl.quitarNueva(x.id)}
              esPerfil={ctrl.clavePerfil === ctrl.claveDe(true, x.id)} onPerfil={() => ctrl.elegirPerfil(true, x.id)}
            />
          ))}
        </Box>
      )}
      {ctrl.hayCambios && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 12, color: "warning.main", fontWeight: 600 }}>
            Cambios pendientes
            {ctrl.nuevas.length ? ` · ${ctrl.nuevas.length} por subir (${resumenAhorro(ctrl.nuevas)})` : ""}
            {ctrl.quitar.length ? ` · ${ctrl.quitar.length} por eliminar` : ""}
            {" — se aplican al pulsar Guardar."}
          </Typography>
          {ctrl.quitar.length > 0 && <Button size="small" sx={{ py: 0, fontSize: 11 }} onClick={ctrl.deshacerQuitar} disabled={bloqueado}>Deshacer eliminación</Button>}
        </Box>
      )}
      <VisorImagen imagen={visor} onClose={() => setVisor(null)} />
    </Box>
  );
}

/* ---------- Solo lectura (ficha de información) ---------- */

export default function ImagenesServicioPanel({ codCliente, codServicioCliente }) {
  const [imagenes, setImagenes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [visor, setVisor] = useState(null);

  useEffect(() => {
    let vivo = true;
    clienteService.getImagenesServicio(codCliente, codServicioCliente)
      .then(({ data }) => vivo && setImagenes(Array.isArray(data) ? data : []))
      .catch((e) => vivo && setError(e?.response?.data?.message || "No se pudieron cargar las imágenes."))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, [codCliente, codServicioCliente]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Imágenes del servicio</Typography>
        {cargando && <CircularProgress size={14} />}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
      {!cargando && !error && imagenes.length === 0 && <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Este servicio no tiene imágenes.</Typography>}
      {imagenes.length > 0 && (
        <Box sx={GRID_SX}>
          {imagenes.map((img) => (
            <Miniatura
              key={img.id} src={urlImagen(img)} titulo={img.nombre_original} pie={formatearPeso(img.tamano)}
              onClick={() => setVisor({ src: urlImagen(img), titulo: img.nombre_original })} esPerfil={Number(img.principal) === 1}
            />
          ))}
        </Box>
      )}
      <VisorImagen imagen={visor} onClose={() => setVisor(null)} />
    </Box>
  );
}
